import { RECORDABLE_PROCESSORS } from '@/services/payments';
import { CHARGE_IDENTIFIER_SYSTEM, writePaymentAttribution } from '@/services/paymentProvenance';
import { fetchBot } from '@/services/services';
import { Alert, Button, Group, Modal, NumberInput, Select, Stack, TextInput } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { createReference } from '@medplum/core';
import { Patient, Practitioner } from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import { ReactElement, useState } from 'react';

const BOT_NAME = 'create-treatment-payment';

/** Today as `YYYY-MM-DD`, the shape Mantine's DateInput works in. */
function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export interface RecordPaymentModalProps {
  patient: Patient;
  opened: boolean;
  onClose: () => void;
  /** Called once the settlement is recorded, so the caller can re-read the order. */
  onRecorded: () => Promise<void>;
}

/**
 * Records a settlement that arrived without a webhook — a Cherry portal payment
 * or a CareCredit one.
 *
 * Stripe is deliberately absent from the processor list: its webhook already
 * writes its charges, so a hand-entered one would count the same money twice.
 *
 * The settled-on date is asked for separately from the moment of recording,
 * because they genuinely differ — a CareCredit settlement is typically keyed in
 * days after the processor cleared it, and reconciling against the processor's
 * export needs the processor's date.
 */
export function RecordPaymentModal({ patient, opened, onClose, onRecorded }: RecordPaymentModalProps): ReactElement {
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Practitioner | undefined;

  const [processor, setProcessor] = useState<string | null>(null);
  const [amount, setAmount] = useState<number | string>('');
  const [processorRefId, setProcessorRefId] = useState('');
  // Mantine v8's DateInput speaks 'YYYY-MM-DD' strings, not Date objects.
  const [settledOn, setSettledOn] = useState<string | null>(todayIso());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const numericAmount = typeof amount === 'number' ? amount : Number.parseFloat(amount);
  const canRecord = !!processor && numericAmount > 0 && !!processorRefId.trim() && !!settledOn;

  async function record(): Promise<void> {
    if (!canRecord || !settledOn) {
      return;
    }

    setBusy(true);
    setError(undefined);

    try {
      const { recorded } = await fetchBot<{
        recorded: { chargeId: string; checkoutSessionId: string; balance: number | null };
      }>(medplum, BOT_NAME, {
        operation: 'recordPayment',
        patient: createReference(patient),
        paymentProcessor: processor,
        amount: numericAmount,
        processorRefId: processorRefId.trim(),
        settledOn: new Date(`${settledOn}T00:00:00.000Z`).toISOString(),
      });

      // Attribution, not a receipt: the money is already in the payments DB by
      // now, so this is allowed to fail quietly rather than tell the PAC their
      // settlement did not go through and have them key it twice.
      await writePaymentAttribution(medplum, {
        system: CHARGE_IDENTIFIER_SYSTEM,
        value: recorded.chargeId,
        profile,
        component: 'RecordPaymentModal',
      });

      await onRecorded();
      reset();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not record the payment');
    } finally {
      setBusy(false);
    }
  }

  function reset(): void {
    setProcessor(null);
    setAmount('');
    setProcessorRefId('');
    setSettledOn(todayIso());
    setError(undefined);
  }

  return (
    <Modal opened={opened} onClose={onClose} title="Record a payment" centered size="sm">
      <Stack>
        {error && <Alert color="red">{error}</Alert>}

        <Select
          label="Processor"
          placeholder="Select a processor"
          data={RECORDABLE_PROCESSORS}
          value={processor}
          onChange={setProcessor}
        />

        <NumberInput
          label="Amount"
          prefix="$"
          decimalScale={2}
          min={0}
          value={amount}
          onChange={setAmount}
          thousandSeparator
        />

        <TextInput
          label="Transaction ID"
          placeholder="The processor's own reference"
          value={processorRefId}
          onChange={(event) => setProcessorRefId(event.currentTarget.value)}
        />

        {/* Distinct from when this card is written — see the note on the
            component. Reconciliation uses the processor's date. */}
        <DateInput label="Settled on" value={settledOn} onChange={setSettledOn} maxDate={todayIso()} />

        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={record} loading={busy} disabled={!canRecord}>
            Record payment
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
